import React from 'react';

const GlobalStyles = () => (
  <style>{`
    :root {
      --neon-primary: #00f2ea;
      --neon-secondary: #ff0050;
      --neon-warning: #ffb700;
      --neon-purple: #b000ff;
      --bg-deep: #050510;
      --ios-blur: blur(24px);
      --font-main: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
      --sat: env(safe-area-inset-top);
      --sab: env(safe-area-inset-bottom);
    }

    body {
      background-color: var(--bg-deep);
      color: #e2e8f0;
      font-family: var(--font-main);
      overflow: hidden;
      -webkit-font-smoothing: antialiased;
    }

    .liquid-bg-container {
      position: absolute; inset: 0; overflow: hidden; z-index: 0;
      background: radial-gradient(circle at 50% 120%, #1a103c 0%, #000000 70%);
    }
    .liquid-orb {
      position: absolute; border-radius: 50%; filter: blur(90px); opacity: 0.5;
      animation: float 20s infinite ease-in-out;
    }
    .orb-1 { width: 60vw; height: 60vw; background: #00f2ea; top: -20%; left: -10%; animation-delay: 0s; }
    .orb-2 { width: 50vw; height: 50vw; background: #b000ff; bottom: -10%; right: -10%; animation-delay: -5s; }
    .orb-3 { width: 30vw; height: 30vw; background: #ff0050; top: 40%; left: 30%; animation-delay: -10s; opacity: 0.3; }

    @keyframes float {
      0% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(30px, 50px) scale(1.1); }
      100% { transform: translate(-20px, 20px) scale(0.9); }
    }

    .glass-card {
      background: rgba(255, 255, 255, 0.03);
      backdrop-filter: blur(30px) saturate(180%);
      -webkit-backdrop-filter: blur(30px) saturate(180%);
      border: 1px solid rgba(255, 255, 255, 0.08);
      box-shadow: 0 20px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1);
    }

    .glass-input {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.15);
      box-shadow: inset 0 2px 5px rgba(0,0,0,0.2);
      color: white;
    }
    .glass-input:focus {
      border-color: var(--neon-primary);
      box-shadow: 0 0 20px rgba(0, 242, 234, 0.2), inset 0 2px 5px rgba(0,0,0,0.2);
    }

    .glow-text-white { text-shadow: 0 0 10px rgba(255,255,255,0.5); }
    .glow-text-cyan { text-shadow: 0 0 15px rgba(0, 242, 234, 0.6); }

    .msg-enter { animation: slideUp 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) forwards; opacity: 0; transform: translateY(20px); }
    @keyframes slideUp { to { transform: translateY(0); opacity: 1; } }

    .no-scrollbar::-webkit-scrollbar { display: none; }
    .safe-top { padding-top: max(12px, var(--sat)); }
    .safe-bottom { padding-bottom: max(24px, var(--sab)); }

    .cyber-grid {
      background-size: 40px 40px;
      background-image: linear-gradient(to right, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                        linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
      position: absolute; inset: 0; pointer-events: none; z-index: 1;
    }

    .cyber-range {
      -webkit-appearance: none; width: 100%; height: 4px; background: rgba(255,255,255,0.1); border-radius: 2px; outline: none;
    }
    .cyber-range::-webkit-slider-thumb {
      -webkit-appearance: none; width: 16px; height: 16px; background: var(--neon-primary);
      box-shadow: 0 0 10px var(--neon-primary); cursor: pointer; border-radius: 50%;
    }

    /* Sidebar animations */
    .animate-fade-in {
      animation: fadeIn 0.3s ease-out forwards;
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .animate-slide-in {
      animation: slideIn 0.35s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
    }
    @keyframes slideIn {
      from { transform: translateX(-100%); }
      to { transform: translateX(0); }
    }

    input, textarea {
      autocomplete: off;
      autocorrect: off;
      autocapitalize: off;
      spellcheck: false;
    }

    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    textarea:-webkit-autofill,
    textarea:-webkit-autofill:hover,
    textarea:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0px 1000px var(--bg-deep) inset !important;
      -webkit-text-fill-color: white !important;
      transition: background-color 5000s ease-in-out 0s;
    }

    /* Disable auto-fill bar in mobile browsers */
    input::-webkit-contacts-auto-fill-button,
    input::-webkit-credentials-auto-fill-button {
      visibility: hidden;
      display: none !important;
      pointer-events: none;
      position: absolute;
      right: 0;
    }

    input::-webkit-textfield-decoration-container {
      display: none !important;
    }

    input[type="text"],
    input[type="email"],
    input[type="password"] {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }

    input:-webkit-autofill,
    input:-webkit-autofill:hover, 
    input:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
      -webkit-text-fill-color: inherit !important;
      transition: background-color 5000s ease-in-out 0s;
    }

    input::-webkit-contact-auto-fill-button,
    input::-webkit-credentials-auto-fill-button,
    input::-webkit-caps-lock-indicator {
      visibility: hidden;
      pointer-events: none;
      position: absolute;
      right: 0;
    }

    input[autocomplete="off"]::-webkit-contacts-auto-fill-button,
    input[autocomplete="off"]::-webkit-credentials-auto-fill-button {
      visibility: hidden !important;
      display: none !important;
      pointer-events: none !important;
    }

    @supports (-webkit-touch-callout: none) {
      body {
        min-height: -webkit-fill-available;
      }
    }
  `}</style>
);

export default GlobalStyles;
